import {
  BookOpenText as BookIcon,
  Ear as EarIcon,
  Microphone as MicrophoneIcon,
  SmileySticker as SmileyIcon,
  Brain as BrainIcon,
  Rocket as RocketIcon,
  ChartBar as ChartIcon,
} from "@phosphor-icons/react";

import Link from "next/link";

type FeatureCardProps = {
  icon: any;
  title: string;
  description: string;
};

function FeatureCard({ icon: Icon, title, description }: FeatureCardProps) {
  return (
    <div className="bg-white p-5 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
      <div className="flex items-center mb-3">
        <Icon size={28} className="text-indigo-600" />
        <h3 className="ml-3 font-medium text-lg text-neutral-800">{title}</h3>
      </div>
      <p className="text-neutral-600 text-sm">{description}</p>
    </div>
  );
}

type ModelSectionProps = {
  iconClass: any;
  name: string;
  page: string;
};

function ModelSection(props: ModelSectionProps) {
  return (
    <Link href={props.page}>
      <div className="hover:border-neutral-400 hover:ease-linear duration-200 flex w-full justify-center items-center rounded-lg border border-neutral-200 bg-white px-14 py-12 shadow">
        <props.iconClass size={40} />
        <div className="ml-6 text-xl">{props.name}</div>
      </div>
    </Link>
  );
}

export default function HomePage() {
  return (
    <div className="px-6 py-10 pb-20 sm:px-10 md:px-14">
      <div className="text-center md:text-left">
        <div className="pb-2 text-5xl font-medium text-neutral-700">AI+EI</div>
        <div className="pt-2 text-2xl font-medium text-neutral-600">Bringing Emotional Intelligence to Artificial Intelligence</div>
        
        <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl shadow-md">
          <h2 className="text-2xl font-medium text-neutral-800 mb-4">Transform your AI with the Power of Emotional Intelligence</h2>
          <p className="text-neutral-700 mb-3">AI+EI bridges the gap between artificial intelligence and emotional intelligence, enabling AI systems to understand, interpret, and respond to human emotions across multiple modalities.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
            <FeatureCard 
              icon={BrainIcon} 
              title="Multi-modal Analysis" 
              description="Detect emotions across facial expressions, speech patterns, vocal bursts, and written text."
            />
            <FeatureCard 
              icon={RocketIcon} 
              title="Real-time Processing" 
              description="Get immediate emotional analysis with continuous monitoring and responsive visualization."
            />
            <FeatureCard 
              icon={ChartIcon} 
              title="Rich Emotional Data" 
              description="Access 50+ emotional states beyond basic emotions with nuanced intensity measurement."
            />
          </div>
          
          <div className="mt-8 p-4 bg-indigo-100 rounded-lg">
            <h3 className="font-medium text-lg mb-2">Perfect for:</h3>
            <ul className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 text-neutral-700">
              <li>✓ Enhanced Customer Service</li>
              <li>✓ Mental Health Applications</li>
              <li>✓ Education Technology</li>
              <li>✓ Market Research Tools</li>
              <li>✓ Interactive Entertainment</li>
              <li>✓ Accessibility Solutions</li>
            </ul>
          </div>
        </div>
        
        <h2 className="mt-12 mb-6 text-2xl font-medium text-neutral-700">Try our emotion recognition technology:</h2>

        <div className="md:px-10 pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <ModelSection name="Facial Expression" page="/face" iconClass={SmileyIcon} />
          <ModelSection name="Speech Prosody" page="/prosody" iconClass={EarIcon} />
          <ModelSection name="Vocal Burst" page="/burst" iconClass={MicrophoneIcon} />
          <ModelSection name="Written Language" page="/language" iconClass={BookIcon} />
        </div>
      </div>
    </div>
  );
}


